// Motor de rotación de la pantalla de planta (P1).
//
// ⚠️ POR QUÉ ESTE HOOK EXISTE (bug real en prod, 2026-08-20): el timer vivía
// dentro de PantallaPage con `data` (el objeto de TanStack Query) en las
// dependencias del efecto. El `refetchInterval` de 30 s entrega una referencia
// NUEVA en cada poll → el efecto se re-ejecutaba y reiniciaba el `setInterval`
// justo antes de disparar. Con rotación de 30 s la TV no rotaba NUNCA.
//
// El contrato de este hook es SOLO PRIMITIVOS: así es imposible volver a
// colgar el timer de un objeto que cambia de identidad en cada refresco.
// `useRotacion.test.ts` fija esa propiedad.

import { useCallback, useEffect, useRef, useState } from 'react';

interface OpcionesRotacion {
  /** false = no rota (sin datos, panel único, auto apagado o alerta tomando la pantalla). */
  activo: boolean;
  /** Segundos entre paneles. */
  segundos: number;
  /** Cantidad de paneles en la rotación. */
  total: number;
}

interface Rotacion {
  /** Índice del panel en rotación. */
  idx: number;
  /** Se incrementa en cada cambio; reinicia la animación de progreso del pie. */
  ciclo: number;
  /** Panel forzado desde C15 (previsualización), aunque esté fuera de la rotación. */
  forzado: string | null;
  /** Navegación manual: gana sobre la rotación (y sobre la alerta) por dos ciclos. */
  irA: (i: number) => void;
  /** Fuerza un panel puntual (previsualización desde C15). */
  forzar: (clave: string) => void;
  /** ¿La navegación manual sigue teniendo prioridad? */
  enPausa: boolean;
}

/** Ciclos que la navegación manual se impone sobre el auto y sobre la alerta. */
const CICLOS_DE_PAUSA_MANUAL = 2;

export function useRotacion({ activo, segundos, total }: OpcionesRotacion): Rotacion {
  const [idx, setIdx] = useState(0);
  const [ciclo, setCiclo] = useState(0);
  const [forzado, setForzado] = useState<string | null>(null);
  const pausaHasta = useRef(0);
  // Espejo en estado para que `enPausa` se re-evalúe al vencer la pausa.
  const [, setLatido] = useState(0);

  const pausar = useCallback(() => {
    pausaHasta.current = Date.now() + segundos * 1000 * CICLOS_DE_PAUSA_MANUAL;
    setLatido((n) => n + 1);
  }, [segundos]);

  const irA = useCallback(
    (i: number) => {
      if (total <= 0) return;
      setForzado(null);
      setIdx(((i % total) + total) % total);
      setCiclo((c) => c + 1);
      pausar();
    },
    [total, pausar],
  );

  const forzar = useCallback(
    (clave: string) => {
      setForzado(clave);
      setCiclo((c) => c + 1);
      pausar();
    },
    [pausar],
  );

  useEffect(() => {
    if (!activo || total <= 1) return;
    const t = window.setInterval(() => {
      if (Date.now() < pausaHasta.current) return;
      setForzado(null);
      setIdx((i) => (i + 1) % total);
      setCiclo((c) => c + 1);
    }, segundos * 1000);
    return () => window.clearInterval(t);
  }, [activo, segundos, total]);

  // Latido de 1 s: vence la pausa manual sin depender de un render externo.
  useEffect(() => {
    const t = window.setInterval(() => setLatido((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, []);

  return {
    idx: total <= 0 ? 0 : Math.min(idx, total - 1),
    ciclo,
    forzado,
    irA,
    forzar,
    enPausa: Date.now() < pausaHasta.current,
  };
}
